import type { ExtendedBid } from '@/features/bidding/types/bid'
import type { EnergyBidPtuSnapshot, EnergyBidSnapshot } from '@/features/bidding/types/bidDetail/EnergyBidSnapshot'

/**
 * Adapter: an `ExtendedBid` (the existing bidding-feature shape) → the uniform
 * `EnergyBidSnapshot` the view consumes.
 *
 * - `ExtendedBidPtu.ptuChunks` (price-ladder rungs) → `EnergyBidStepSnapshot[]`
 * - `ExtendedBidPtu.offeredVolume` total is not surfaced separately; it can
 *   be computed by summing steps when needed.
 * - `source` defaults to `'UNKNOWN'` — pending Q4 (backend field name).
 */
export function toEnergyBidSnapshot(bid: ExtendedBid): EnergyBidSnapshot {
  const ptus: EnergyBidPtuSnapshot[] = bid.ptus.map((ptu) => ({
    ptu: { start: ptu.ptu.start, end: ptu.ptu.end },
    steps: (ptu.ptuChunks ?? []).map((chunk) => ({
      offeredVolume: chunk.offeredVolume,
      acknowledgedVolume: chunk.acceptedVolume,
      offeredPrice: chunk.offeredPrice,
      acknowledgedPrice: chunk.acceptedPrice,
    })),
    source: 'UNKNOWN',
  }))

  return {
    context: {
      portfolioCode: bid.portfolio.code,
      deliveryDay: bid.deliveryDay.toISODate(),
      marketProgram: bid.marketProgram,
      activationGroupUuid: bid.activationGroupUuid,
      activationGroupCode: bid.activationGroupName ?? bid.activationGroupUuid,
      bidType: bid.bidType,
    },
    ptus,
  }
}
